import { __extends } from "tslib";
import { getShadowElement } from '@tyler-components-web/core';
import { deepQuerySelectorAll, getActiveElement } from 'shadow-utils';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { AUTOCOMPLETE_CONSTANTS } from './autocomplete-constants';
import { TEXT_FIELD_CONSTANTS } from '../text-field';
import { ListDropdown } from '../list-dropdown';
import { CHIP_FIELD_CONSTANTS } from '../chip-field';
import { POPUP_CONSTANTS } from '../popup';
/**
 * The DOM adapter for the `<tcw-autocomplete>` element.
 */
var AutocompleteAdapter = /** @class */ (function (_super) {
    __extends(AutocompleteAdapter, _super);
    function AutocompleteAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this.setInputElement();
        return _this;
    }
    AutocompleteAdapter.prototype.setInputElement = function () {
        var inputElements = deepQuerySelectorAll(this._component, AUTOCOMPLETE_CONSTANTS.selectors.INPUT, false);
        if (inputElements.length) {
            this._inputElement = inputElements[0];
        }
        return this._inputElement;
    };
    AutocompleteAdapter.prototype.setInputAttribute = function (name, value) {
        this._inputElement.setAttribute(name, value);
    };
    AutocompleteAdapter.prototype.addInputListener = function (type, listener) {
        this._inputElement.addEventListener(type, listener);
    };
    AutocompleteAdapter.prototype.removeInputListener = function (type, listener) {
        this._inputElement.removeEventListener(type, listener);
    };
    AutocompleteAdapter.prototype.intializeInputAccessibility = function (identifier) {
        this._inputElement.setAttribute('autocomplete', 'off');
        this._inputElement.setAttribute('role', 'combobox');
        this._inputElement.setAttribute('aria-live', 'polite');
        this._inputElement.setAttribute('aria-atomic', 'true');
        this._inputElement.setAttribute('aria-haspopup', 'true');
        this._inputElement.setAttribute('aria-expanded', 'false');
        this._inputElement.setAttribute('aria-autocomplete', 'list');
    };
    AutocompleteAdapter.prototype.isWrappingChipField = function () {
        return !!this._component.querySelector(CHIP_FIELD_CONSTANTS.elementName);
    };
    AutocompleteAdapter.prototype.show = function (config, popupTarget) {
        this._targetElement = this._getTargetElement(popupTarget);
        if (!this._targetElement) {
            return;
        }
        this._listDropdown = new ListDropdown(this._targetElement, config);
        this._listDropdown.open();
        this._inputElement.setAttribute('aria-expanded', 'true');
        this._inputElement.setAttribute('aria-controls', "list-dropdown-popup-" + config.id);
    };
    AutocompleteAdapter.prototype.hide = function (listener) {
        if (!this._listDropdown) {
            return;
        }
        this._listDropdown.dropdownElement.targetElement.removeEventListener(POPUP_CONSTANTS.events.BLUR, listener);
        this.setBusyVisibility(false);
        this._inputElement.removeAttribute('aria-activedescendant');
        this._inputElement.removeAttribute('aria-controls');
        this._inputElement.setAttribute('aria-expanded', 'false');
        this._listDropdown.close();
        this._listDropdown = undefined;
        return;
    };
    AutocompleteAdapter.prototype.setBusyVisibility = function (isVisible) {
        if (this._listDropdown) {
            this._listDropdown.setBusyVisibility(isVisible);
        }
    };
    AutocompleteAdapter.prototype.toggleOptionMultiple = function (index, selected) {
        if (this._listDropdown) {
            this._listDropdown.toggleOptionMultiple(index, selected);
        }
    };
    AutocompleteAdapter.prototype.setDismissListener = function (listener) {
        if (!this._listDropdown || !this._listDropdown.dropdownElement) {
            return;
        }
        var dropdownElement = this._listDropdown.dropdownElement;
        if (dropdownElement.targetElement) {
            dropdownElement.targetElement.addEventListener(POPUP_CONSTANTS.events.BLUR, listener);
        }
    };
    AutocompleteAdapter.prototype.focus = function () {
        var _this = this;
        window.requestAnimationFrame(function () { return _this._inputElement.focus(); });
    };
    AutocompleteAdapter.prototype.setOptions = function (options) {
        if (this._listDropdown) {
            this._listDropdown.setOptions(options);
        }
    };
    AutocompleteAdapter.prototype.appendOptions = function (options) {
        if (this._listDropdown) {
            this._listDropdown.appendOptions(options);
        }
    };
    AutocompleteAdapter.prototype.setSelectedText = function (value) {
        this._inputElement.value = value;
    };
    AutocompleteAdapter.prototype.getInputValue = function () {
        return this._inputElement.value;
    };
    AutocompleteAdapter.prototype.selectInputValue = function () {
        var _this = this;
        window.requestAnimationFrame(function () { return _this._inputElement.select(); });
    };
    AutocompleteAdapter.prototype.hasFocus = function () {
        var activeElement = getActiveElement();
        return activeElement === this._inputElement;
    };
    AutocompleteAdapter.prototype.hasInputElement = function () {
        return !!this._inputElement;
    };
    AutocompleteAdapter.prototype.setDropdownIconListener = function (type, listener) {
        var _this = this;
        window.requestAnimationFrame(function () {
            var dropdownIcon = _this._component.querySelector(AUTOCOMPLETE_CONSTANTS.selectors.DROPDOWN_ICON);
            if (dropdownIcon) {
                dropdownIcon.addEventListener(type, listener);
            }
        });
    };
    AutocompleteAdapter.prototype.removeDropdownIconListener = function (type, listener) {
        var dropdownIcon = this._component.querySelector(AUTOCOMPLETE_CONSTANTS.selectors.DROPDOWN_ICON);
        if (dropdownIcon) {
            dropdownIcon.removeEventListener(type, listener);
        }
    };
    AutocompleteAdapter.prototype.propagateKey = function (key) {
        if (this._listDropdown) {
            this._listDropdown.handleKey(key);
        }
    };
    AutocompleteAdapter.prototype.updateActiveDescendant = function (id) {
        if (!this._targetElement) {
            return;
        }
        if (id) {
            this._inputElement.setAttribute('aria-activedescendant', id);
        }
        else {
            this._inputElement.removeAttribute('aria-activedescendant');
        }
    };
    AutocompleteAdapter.prototype.getTargetElementWidth = function (selector) {
        if (!this._targetElement) {
            this._targetElement = this._getTargetElement(selector);
        }
        return this._targetElement.getBoundingClientRect().width;
    };
    AutocompleteAdapter.prototype.getPopupElement = function () {
        return this._listDropdown ? this._listDropdown.dropdownElement || null : null;
    };
    AutocompleteAdapter.prototype.activateInitialOption = function () {
        if (this._listDropdown) {
            this._listDropdown.activateInitialOption();
        }
    };
    AutocompleteAdapter.prototype.getActiveOptionIndex = function () {
        return this._listDropdown ? this._listDropdown.getActiveOptionIndex() : null;
    };
    AutocompleteAdapter.prototype.clearActiveOption = function () {
        if (this._listDropdown) {
            this._listDropdown.clearActiveOption();
        }
    };
    AutocompleteAdapter.prototype.setSelectedOptions = function (options) {
        if (this._listDropdown) {
            var values = options.map(function (o) { return o.value; });
            this._listDropdown.setSelectedValues(values);
        }
    };
    AutocompleteAdapter.prototype._getTargetElement = function (selector) {
        return selector ? this._component.querySelector(selector) || this._getDefaultTargetElement() : this._getDefaultTargetElement();
    };
    AutocompleteAdapter.prototype._getDefaultTargetElement = function () {
        // This component is often used with the text-field, if so, let's target our popup around one if its internal elements for proper alignnment
        var textField = this._component.querySelector(TEXT_FIELD_CONSTANTS.elementName);
        if (textField && textField.shadowRoot) {
            var textFieldRoot = getShadowElement(textField, TEXT_FIELD_CONSTANTS.selectors.ROOT);
            if (textFieldRoot) {
                return textFieldRoot;
            }
        }
        // This component is often used with the chip-field, if so, let's target our popup around one if its internal elements for proper alignnment
        var chipField = this._component.querySelector(CHIP_FIELD_CONSTANTS.elementName);
        if (chipField && chipField.shadowRoot) {
            var chipFieldRoot = getShadowElement(chipField, CHIP_FIELD_CONSTANTS.selectors.CONTENT);
            if (chipFieldRoot) {
                return chipFieldRoot;
            }
        }
        return this._component.querySelector('input') || this._component;
    };
    return AutocompleteAdapter;
}(BaseAdapter));
export { AutocompleteAdapter };
